import { useQuery } from "@tanstack/react-query";
import { api } from "./client";
import type {
  AnalyticsCommentDto,
  AnalyticsFilterDto,
  AnalyticsOverviewDto,
  AnomalyPointDto,
  CorrelationPointDto,
  CsatTrendPointDto,
  HeatmapPointDto,
  KeywordClusterDto,
  LowScoreOverviewDto,
  LowScoreReasonPointDto,
  NpsTrendPointDto,
  ReviewCtaAnalyticsDto,
  ServiceRecoveryFunnelPointDto,
  SlaTtrPointDto
} from "@hotel-surveys/shared";
import type { Alert } from "./client";

export const analyticsQueryKeys = {
  all: ["analytics"] as const,
  list: (name: string, filters: AnalyticsFilterDto = {}) => [...analyticsQueryKeys.all, name, normalizeFilters(filters)] as const,
  overview: (filters?: AnalyticsFilterDto) => analyticsQueryKeys.list("overview", filters),
  nps: (filters?: AnalyticsFilterDto) => analyticsQueryKeys.list("nps", filters),
  csat: (filters?: AnalyticsFilterDto) => analyticsQueryKeys.list("csat", filters),
  heatmap: (filters?: AnalyticsFilterDto) => analyticsQueryKeys.list("heatmap", filters),
  comments: (filters?: AnalyticsFilterDto) => analyticsQueryKeys.list("comments", filters),
  redFlags: (filters?: AnalyticsFilterDto) => analyticsQueryKeys.list("red-flags", filters),
  lowScore: (filters?: AnalyticsFilterDto) => analyticsQueryKeys.list("low-score", filters),
  lowScoreReasons: (filters?: AnalyticsFilterDto) => analyticsQueryKeys.list("low-score-reasons", filters),
  serviceRecovery: (filters?: AnalyticsFilterDto) => analyticsQueryKeys.list("service-recovery", filters),
  correlation: (filters?: AnalyticsFilterDto) => analyticsQueryKeys.list("correlation", filters),
  anomalies: (filters?: AnalyticsFilterDto) => analyticsQueryKeys.list("anomalies", filters),
  slaTtr: (filters?: AnalyticsFilterDto) => analyticsQueryKeys.list("sla-ttr", filters),
  keywordClusters: (filters?: AnalyticsFilterDto) => analyticsQueryKeys.list("keyword-clusters", filters),
  reviewCta: (filters?: AnalyticsFilterDto) => analyticsQueryKeys.list("review-cta", filters)
};

export function useAnalyticsOverview(filters?: AnalyticsFilterDto) {
  return useQuery({ queryKey: analyticsQueryKeys.overview(filters), queryFn: () => analyticsApi<AnalyticsOverviewDto>("overview", filters) });
}

export function useNpsTrend(filters?: AnalyticsFilterDto) {
  return useQuery({ queryKey: analyticsQueryKeys.nps(filters), queryFn: () => analyticsApi<NpsTrendPointDto[]>("nps", filters) });
}

export function useCsatTrend(filters?: AnalyticsFilterDto) {
  return useQuery({ queryKey: analyticsQueryKeys.csat(filters), queryFn: () => analyticsApi<CsatTrendPointDto[]>("csat", filters) });
}

export function useHeatmap(filters?: AnalyticsFilterDto) {
  return useQuery({ queryKey: analyticsQueryKeys.heatmap(filters), queryFn: () => analyticsApi<HeatmapPointDto[]>("heatmap", filters) });
}

export function useAnalyticsComments(filters?: AnalyticsFilterDto) {
  return useQuery({ queryKey: analyticsQueryKeys.comments(filters), queryFn: () => analyticsApi<AnalyticsCommentDto[]>("comments", filters) });
}

export function useRedFlags(filters?: AnalyticsFilterDto) {
  return useQuery({ queryKey: analyticsQueryKeys.redFlags(filters), queryFn: () => analyticsApi<Alert[]>("red-flags", filters) });
}

export function useLowScoreOverview(filters?: AnalyticsFilterDto) {
  return useQuery({ queryKey: analyticsQueryKeys.lowScore(filters), queryFn: () => analyticsApi<LowScoreOverviewDto>("low-score", filters) });
}

export function useLowScoreReasons(filters?: AnalyticsFilterDto) {
  return useQuery({ queryKey: analyticsQueryKeys.lowScoreReasons(filters), queryFn: () => analyticsApi<LowScoreReasonPointDto[]>("low-score-reasons", filters) });
}

export function useServiceRecovery(filters?: AnalyticsFilterDto) {
  return useQuery({ queryKey: analyticsQueryKeys.serviceRecovery(filters), queryFn: () => analyticsApi<ServiceRecoveryFunnelPointDto[]>("service-recovery-funnel", filters) });
}

export function useCorrelation(filters?: AnalyticsFilterDto) {
  return useQuery({ queryKey: analyticsQueryKeys.correlation(filters), queryFn: () => analyticsApi<CorrelationPointDto[]>("correlation", filters) });
}

export function useAnomalies(filters?: AnalyticsFilterDto) {
  return useQuery({ queryKey: analyticsQueryKeys.anomalies(filters), queryFn: () => analyticsApi<AnomalyPointDto[]>("anomalies", filters) });
}

export function useSlaTtr(filters?: AnalyticsFilterDto) {
  return useQuery({ queryKey: analyticsQueryKeys.slaTtr(filters), queryFn: () => analyticsApi<SlaTtrPointDto[]>("sla-ttr", filters) });
}

export function useKeywordClusters(filters?: AnalyticsFilterDto) {
  return useQuery({ queryKey: analyticsQueryKeys.keywordClusters(filters), queryFn: () => analyticsApi<KeywordClusterDto[]>("keyword-clusters", filters) });
}

export function useReviewCta(filters?: AnalyticsFilterDto) {
  return useQuery({ queryKey: analyticsQueryKeys.reviewCta(filters), queryFn: () => analyticsApi<ReviewCtaAnalyticsDto>("review-cta", filters) });
}

export function analyticsQueryString(filters: AnalyticsFilterDto = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (typeof value === "string" && value.trim()) params.set(key, value.trim());
  });
  return params.toString();
}

function analyticsApi<T>(name: string, filters: AnalyticsFilterDto = {}) {
  const query = analyticsQueryString(filters);
  return api<T>(`/api/admin/analytics/${name}${query ? `?${query}` : ""}`);
}

function normalizeFilters(filters: AnalyticsFilterDto = {}) {
  return Object.fromEntries(Object.entries(filters).filter(([, value]) => typeof value === "string" && value.trim()).sort(([a], [b]) => a.localeCompare(b)));
}
